import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { MediaStory } from "../components/MediaStory";
import { MediaTile } from "../components/MediaTile";
import { apiFetch } from "../lib/api";
import { uploadPhoto } from "../lib/upload";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can } from "@family/shared";
import type { AlbumDetail, MediaRef } from "@family/shared";

export function AlbumDetailPage() {
  const { albumId } = useParams<{ albumId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const query = useQuery<AlbumDetail>({
    queryKey: ["album", slug, albumId],
    queryFn: () => apiFetch<AlbumDetail>(`/families/${slug}/albums/${albumId}`),
    enabled: Boolean(slug && albumId),
  });

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0 || !slug) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    try {
      const mediaIds: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        const mediaId = await uploadPhoto(slug, file, {
          onProgress: (pct) => {
            setUploadProgress(Math.round(((i + pct / 100) / files.length) * 100));
          },
        });
        mediaIds.push(mediaId);
      }
      await apiFetch(`/families/${slug}/albums/${albumId}/media`, {
        method: "POST",
        body: JSON.stringify({ mediaIds }),
      });
      void queryClient.invalidateQueries({ queryKey: ["album", slug, albumId] });
      void queryClient.invalidateQueries({ queryKey: ["albums", slug] });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  if (!primary || !slug) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  const album = query.data?.album;
  const mediaItems = query.data?.media ?? [];
  const canUpload = can(primary.role, "post:create");

  const watermarkVars = {
    user:
      meQuery.data?.user.displayName ??
      meQuery.data?.user.email ??
      meQuery.data?.user.phone ??
      "",
    family: primary.familyName,
    phone: meQuery.data?.user.phone ?? null,
    email: meQuery.data?.user.email ?? null,
  };

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <Link
          to="/albums"
          className="text-muted"
          style={{ textDecoration: "none" }}
        >
          ← Back to albums
        </Link>

        {query.isLoading && <p className="text-muted">Loading…</p>}
        {query.error && (
          <p className="text-error">
            Error: {(query.error as Error).message}
          </p>
        )}

        {album && (
          <>
            <div className="row-between">
              <div>
                <h1 style={{ margin: 0 }}>{album.title}</h1>
                <div
                  className="text-subtle"
                  style={{ fontSize: "0.85rem", marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap" }}
                >
                  <span>
                    {album.mediaCount} photo{album.mediaCount !== 1 ? "s" : ""}
                  </span>
                  {album.eventTitle && <span>📅 {album.eventTitle}</span>}
                  {album.visibility === "groups" && <span>🔒 Group</span>}
                  {album.createdBy.displayName && (
                    <span>Created by {album.createdBy.displayName}</span>
                  )}
                </div>
              </div>
              {canUpload && (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    hidden
                    onChange={(e) => void handleUpload(e)}
                  />
                  <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
                    {can(primary.role, "media:upload") && (
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => navigate(`/scan?album=${album.id}`)}
                        disabled={uploading}
                      >
                        📷 Scan
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? `Uploading… ${uploadProgress}%` : "+ Add photos"}
                    </button>
                  </div>
                </>
              )}
            </div>

            {album.description && (
              <p className="text-muted" style={{ marginTop: "var(--s-2)" }}>
                {album.description}
              </p>
            )}

            {uploadError && <p className="text-error">{uploadError}</p>}

            {mediaItems.length === 0 && !uploading && (
              <p className="text-muted">
                No photos in this album yet.
                {canUpload && " Use “📷 Scan” to photograph prints, or “+ Add photos” to upload pictures."}
              </p>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "var(--s-3)",
              }}
            >
              {mediaItems.map((m) => {
                const mediaRef: MediaRef = {
                  id: m.mediaId,
                  kind: m.kind,
                  viewUrl: m.viewUrl,
                  thumbnailUrl: m.thumbnailUrl ?? null,
                  width: m.width,
                  height: m.height,
                  taggedPersons: [],
                };
                return (
                  <div key={m.id}>
                    <MediaTile
                      media={mediaRef}
                      familySlug={slug}
                      role={primary.role}
                      watermarkConfig={primary.watermark}
                      watermarkVars={watermarkVars}
                      maxHeight={320}
                    />
                    <MediaStory
                      familySlug={slug}
                      mediaId={m.mediaId}
                      caption={m.caption}
                      takenAt={m.takenAt}
                      takenAtPrecision={m.takenAtPrecision}
                      voiceNoteCount={m.voiceNoteCount}
                      currentUserId={meQuery.data?.user.id}
                      canEditAnyTranscript={can(primary.role, "post:delete:any")}
                      canTranscribe={can(primary.role, "media:upload")}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </>
  );
}
